import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    CssBaseline,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    Menu,
    MenuItem,
    Badge,
} from "@mui/material";
import { Menu as MenuIcon, Brightness4, Brightness7, ExitToApp, AccountCircle } from "@mui/icons-material";
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Link as RouterLink, Outlet } from "react-router-dom";
import { AnimatedContent, drawerWidth, Root, StyledDrawer } from "../../styled";
import { useSidebar } from "../../../../hooks/useSidebar";
import { useAuth } from "../../../../hooks/useAuth";
import { MainMenuProps } from "./types";
import { MainLogo } from "../../../../assets/MainLogo";

export function MainMenuContent({ pages }: MainMenuProps) {
    const [open, setOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(false);
    const theme = useTheme();
    const { logout, isAuthenticated } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { activePath } = useSidebar();

    const handleDrawerToggle = () => setOpen(!open);
    const toggleTheme = () => setDarkMode((prev) => !prev);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <CssBaseline />
            <Root>
                <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
                            <MainLogo />
                        </Typography>
                        {isAuthenticated && (
                            <>
                                <IconButton size="large" aria-label="show notifications" color="inherit">
                                    <Badge badgeContent={0} color="error">
                                        <NotificationsIcon />
                                    </Badge>
                                </IconButton>
                                <IconButton
                                    size="large"
                                    aria-label="account of current user"
                                    aria-controls="menu-appbar"
                                    aria-haspopup="true"
                                    onClick={handleMenu}
                                    color="inherit"
                                >
                                    <AccountCircle />
                                </IconButton>
                                <Menu
                                    id="menu-appbar"
                                    anchorEl={anchorEl}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    keepMounted
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                >
                                    <MenuItem onClick={handleClose}>Profile</MenuItem>
                                    <MenuItem onClick={logout}>Logout</MenuItem>
                                </Menu>
                            </>
                        )}
                        <IconButton color="inherit" onClick={toggleTheme}>
                            {darkMode ? <Brightness7 /> : <Brightness4 />}
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* Sidebar */}
                <StyledDrawer variant="persistent" anchor="left" open={open}>
                    <List disablePadding={true}>
                        <Toolbar />
                        {pages.map((page, index) => (
                            <ListItem key={index} disablePadding>
                                <ListItemButton
                                    component={RouterLink}
                                    to={page.path}
                                    selected={activePath === "/" + page.path} // Проверяем активный путь
                                    sx={{
                                        textDecoration: "none",
                                        color: activePath === page.path || activePath.startsWith(page.path)
                                            ? theme.palette.grey[900]
                                            : "inherit",
                                        backgroundColor: activePath === page.path || activePath.startsWith(page.path)
                                            ? theme.palette.grey[300]
                                            : "transparent",
                                        "&:hover": {
                                            backgroundColor: theme.palette.grey[400],
                                        },
                                        "&.Mui-selected": {
                                            backgroundColor: theme.palette.grey[300], // Светло-серый фон для активной вкладки
                                            color: theme.palette.grey[900], // Темный текст
                                        },
                                        "&.Mui-selected:hover": {
                                            backgroundColor: theme.palette.grey[400], // Чуть темнее при наведении
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                                        {page.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={page.name} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                        <ListItem disablePadding>
                            <ListItemButton onClick={logout}>
                                <ListItemIcon><ExitToApp /></ListItemIcon>
                                <ListItemText primary="Logout" />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </StyledDrawer>

                <AnimatedContent
                    open={open}
                    initial={{ marginLeft: open ? drawerWidth : 0 }}
                    animate={{ marginLeft: open ? drawerWidth : 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Outlet />
                </AnimatedContent>
            </Root>
        </>
    );
}
